import { useEffect, useState } from 'react'
import { Aborted, ApiError, api } from '../api/client'
import type { Assessment, Exchange, InstrumentDetail, NewsItem, PriceHistory, Quote,
              ResearchCase, TradeIdea } from '../api/client'

/** Wie viele Zeilen je Karte. Mehr ist eine Liste, und die gibt es hinter „alle". */
export const JE_KARTE = 5

/** Alles, was die Instrumentseite über die Stammdaten hinaus zeigt. */
export interface InstrumentAktivitaet {
  faelle: ResearchCase[]; faelleGesamt: number
  news: NewsItem[]; newsGesamt: number
  ampeln: Assessment[]; ampelnGesamt: number
  ideen: TradeIdea[]; ideenGesamt: number
  /** Der jüngste Kurs je Handelsplatz, nach MIC. */
  kurse: Record<string, Quote>
  /** Die letzten Tage der Historie am Hauptplatz — für die Veränderung. */
  balken: PriceHistory[]
  /** Leer heißt: nicht bekannt, etwa ohne Recht. */
  aufWatchlist: boolean | null
  /** Handelsplatzname nach MIC. */
  boersen: Record<string, string>
}

const LEER: InstrumentAktivitaet = {
  faelle: [], faelleGesamt: 0, news: [], newsGesamt: 0, ampeln: [], ampelnGesamt: 0,
  ideen: [], ideenGesamt: 0, kurse: {}, balken: [], aufWatchlist: null, boersen: {},
}

/** Ein 403 ist keine Störung, sondern eine Auskunft: Dieser Teil bleibt leer. */
async function weich<T>(arbeit: Promise<T>): Promise<T | null> {
  try { return await arbeit } catch (cause) {
    if (cause instanceof Aborted) throw cause
    if (cause instanceof ApiError) return null
    return null
  }
}

// Die Handelsplätze ändern sich nie im Betrieb — einmal je Sitzung.
let boersenCache: Promise<Record<string, string>> | null = null
function boersenHolen(signal: AbortSignal): Promise<Record<string, string>> {
  if (!boersenCache) {
    boersenCache = api.exchanges({ size: 200 }, signal)
      .then((seite) => Object.fromEntries(seite.items.map((e: Exchange) => [e.code, e.alias || e.name])))
      .catch(() => { boersenCache = null; return {} })
  }
  return boersenCache
}

/**
 * Lädt, was zu einem Instrument gehört: Fälle, News, Einschätzungen, Ideen,
 * Kurse, Watchlist, Handelsplatznamen — aus den BESTEHENDEN Endpunkten,
 * parallel, jeder für sich. Wer ein Recht nicht hat, bekommt für diesen
 * Teil nichts, der Rest steht trotzdem.
 */
export function useInstrumentAktivitaet(instrument: InstrumentDetail | null) {
  const [daten, setDaten] = useState<InstrumentAktivitaet>(LEER)
  const [laedt, setLaedt] = useState(false)

  const isin = instrument?.isin ?? null
  const hauptplatz = instrument?.primary_exchange ?? null

  useEffect(() => {
    if (!isin) return
    // Feste Kopien fuer die asynchrone Funktion — TypeScript sieht die
    // Pruefung oben dort nicht.
    const kennung: string = isin
    const platz: string | null = hauptplatz
    let aktuell = true
    const steuerung = new AbortController()
    const signal = steuerung.signal
    setLaedt(true)
    const ref = `isin:${kennung}`

    async function laden() {
      const [faelle, news, ampeln, ideen, kurse, balken, watch, boersen] = await Promise.all([
        weich(api.researchCases({ isin: kennung, sort: 'last_material_update_at', dir: 'desc',
                                  size: JE_KARTE, total: true }, signal)),
        weich(api.news({ ref: [ref], sort: 'published_at', dir: 'desc', size: JE_KARTE, total: true }, signal)),
        weich(api.assessments({ ref, sort: 'assessed_at', dir: 'desc', size: JE_KARTE, total: true }, signal)),
        weich(api.tradeIdeas({ isin: kennung, sort: 'created', dir: 'desc', size: JE_KARTE, total: true }, signal)),
        weich(api.quotes({ isin: [kennung], sort: 'observed_at', dir: 'desc', size: 50 }, signal)),
        platz
          ? weich(api.priceHistory({ isin: [kennung], exchange: platz, sort: 'trading_date',
                                     dir: 'desc', size: 5 }, signal))
          : null,
        weich(api.watchlist({ q: kennung, size: 5 }, signal)),
        boersenHolen(signal),
      ])
      if (!aktuell) return
      // Je Platz der jüngste Kurs: Die Liste kommt jüngste zuerst, der erste
      // Treffer je MIC gewinnt.
      const jeSeite: Record<string, Quote> = {}
      for (const q of kurse?.items ?? []) if (!jeSeite[q.exchange]) jeSeite[q.exchange] = q
      setDaten({
        faelle: faelle?.items ?? [], faelleGesamt: faelle?.total ?? faelle?.items.length ?? 0,
        news: news?.items ?? [], newsGesamt: news?.total ?? news?.items.length ?? 0,
        ampeln: ampeln?.items ?? [], ampelnGesamt: ampeln?.total ?? ampeln?.items.length ?? 0,
        ideen: ideen?.items ?? [], ideenGesamt: ideen?.total ?? ideen?.items.length ?? 0,
        kurse: jeSeite,
        balken: balken?.items ?? [],
        aufWatchlist: watch ? watch.items.some((w) => w.isin === kennung && w.active) : null,
        boersen,
      })
      setLaedt(false)
    }
    laden().catch(() => { if (aktuell) setLaedt(false) })
    return () => { aktuell = false; steuerung.abort() }
  }, [isin, hauptplatz])

  return { daten, laedt }
}
